import { ENGINE_BASE, engineFetch } from "./config";

const MAX_CONCURRENT = 3;

const cache = new Map<string, string>();
/** Session-scoped misses (404 and other failures). A reload may try again. */
const failed = new Map<string, number>();
const inflight = new Map<string, Promise<string>>();
const waitQueue: Array<{
  url: string;
  resolve: (src: string) => void;
  reject: (err: unknown) => void;
}> = [];
let active = 0;

/** Resolve list thumbnail path against engine base (dev proxy uses ""). */
export function resolveThumbUrl(path: string): string {
  if (
    path.startsWith("http://") ||
    path.startsWith("https://") ||
    path.startsWith("blob:") ||
    path.startsWith("data:")
  ) {
    return path;
  }
  const base = ENGINE_BASE.replace(/\/+$/, "");
  return `${base}${path.startsWith("/") ? path : `/${path}`}`;
}

export function peekCatalogThumb(path: string): string | null {
  return cache.get(resolveThumbUrl(path)) ?? null;
}

/** True when this URL already failed in this session — do not fetch again. */
export function catalogThumbMiss(path: string): boolean {
  return failed.has(resolveThumbUrl(path));
}

/** Drop a decoded blob that is not a usable image and remember the miss. */
export function rememberCatalogThumbMiss(path: string, status = 404): void {
  const url = resolveThumbUrl(path);
  const obj = cache.get(url);
  if (obj) {
    URL.revokeObjectURL(obj);
    cache.delete(url);
  }
  failed.set(url, status);
}

/** Test helper. Revokes object URLs and clears in-flight work. */
export function resetCatalogThumbCacheForTests(): void {
  for (const url of cache.values()) URL.revokeObjectURL(url);
  cache.clear();
  failed.clear();
  inflight.clear();
  waitQueue.length = 0;
  active = 0;
}

function pump(): void {
  while (active < MAX_CONCURRENT && waitQueue.length > 0) {
    const task = waitQueue.shift()!;
    active += 1;
    void (async () => {
      try {
        const res = await engineFetch(task.url);
        if (!res.ok) {
          failed.set(task.url, res.status);
          throw new Error(String(res.status));
        }
        const blob = await res.blob();
        const type = (blob.type || res.headers.get("content-type") || "").toLowerCase();
        const image = type === "" || type.startsWith("image/") || type.includes("svg");
        if (!image || blob.size === 0) {
          failed.set(task.url, 404);
          throw new Error("404");
        }
        const objectUrl = URL.createObjectURL(blob);
        cache.set(task.url, objectUrl);
        task.resolve(objectUrl);
      } catch (err) {
        if (!failed.has(task.url)) failed.set(task.url, 0);
        task.reject(err);
      } finally {
        inflight.delete(task.url);
        active -= 1;
        pump();
      }
    })();
  }
}

/**
 * Auth-aware catalog thumbnail fetch with shared cache and limited concurrency.
 * Object URLs are kept for the session (do not revoke on unmount).
 */
export function loadCatalogThumb(path: string): Promise<string> {
  const url = resolveThumbUrl(path);
  const hit = cache.get(url);
  if (hit) return Promise.resolve(hit);
  if (failed.has(url)) return Promise.reject(new Error(String(failed.get(url))));

  const existing = inflight.get(url);
  if (existing) return existing;

  const promise = new Promise<string>((resolve, reject) => {
    waitQueue.push({ url, resolve, reject });
    pump();
  });
  inflight.set(url, promise);
  return promise;
}

/** Fetch thumb and decode so the card can appear with the image already painted. */
export async function prepareCatalogThumb(path: string): Promise<string> {
  const objectUrl = await loadCatalogThumb(path);
  await new Promise<void>((resolve, reject) => {
    const img = new Image();
    img.onload = () => {
      if (typeof img.decode === "function") {
        void img.decode().then(() => resolve(), () => resolve());
      } else {
        resolve();
      }
    };
    img.onerror = () => reject(new Error("thumb decode failed"));
    img.src = objectUrl;
  });
  return objectUrl;
}

/** Run async work over items with a fixed concurrency limit. */
export async function mapPool<T, R>(
  items: readonly T[],
  limit: number,
  fn: (item: T, index: number) => Promise<R>,
): Promise<R[]> {
  const results = new Array<R>(items.length);
  let next = 0;
  const workers = Array.from({ length: Math.min(limit, items.length) }, async () => {
    while (next < items.length) {
      const i = next;
      next += 1;
      results[i] = await fn(items[i]!, i);
    }
  });
  await Promise.all(workers);
  return results;
}
